/* =========================================================================
   Brooks Halloway — theme scripts
   Vanilla JS, no dependencies. Everything is defensive: if an element
   isn't on the page, the corresponding behaviour simply doesn't bind.
   ========================================================================= */
(function () {
  'use strict';

  var on = function (el, evt, fn) { if (el) el.addEventListener(evt, fn); };
  // Binds once per element, so a theme-editor section reload can't stack
  // duplicate handlers on an element that wasn't re-rendered.
  var once = function (el, key) {
    if (!el) return false;
    var k = 'bound' + key;
    if (el.dataset[k] === 'true') return false;
    el.dataset[k] = 'true';
    return true;
  };
  // Document/window listeners are keyed, so a re-run replaces the old one
  // instead of adding another.
  var globalHandlers = {};
  var onGlobal = function (key, target, evt, fn, opts) {
    var prev = globalHandlers[key];
    if (prev) prev.target.removeEventListener(prev.evt, prev.fn, prev.opts);
    target.addEventListener(evt, fn, opts);
    globalHandlers[key] = { target: target, evt: evt, fn: fn, opts: opts };
  };
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function focusables(root) {
    return $$(FOCUSABLE, root).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  /* ------------------------------------------------------ Mobile menu --- */
  function initMobileMenu() {
    var menu = $('#MobileMenu');
    if (!once(menu, 'Menu')) return;
    var openBtn = $('#MobileMenuToggle');
    var closeBtn = $('#MobileMenuClose');

    function open() {
      menu.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      menu.setAttribute('aria-hidden', 'false');
      menu.removeAttribute('inert');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var first = focusables(menu)[0];
      if (first) first.focus();
    }
    function close() {
      menu.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      menu.setAttribute('aria-hidden', 'true');
      menu.setAttribute('inert', '');
      if (openBtn) {
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.focus();
      }
    }

    on(openBtn, 'click', open);
    on(closeBtn, 'click', close);
    onGlobal('menuKeys', document, 'keydown', function (e) {
      if (!menu.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      // Keep focus inside the drawer while it is open.
      var items = focusables(menu);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------------------------------------------------- Search drawer --- */
  function initSearch() {
    var drawer = $('#SearchDrawer');
    if (!once(drawer, 'Search')) return;
    var toggle = $('#SearchToggle');

    on(toggle, 'click', function () {
      var isHidden = drawer.hasAttribute('hidden');
      if (isHidden) {
        drawer.removeAttribute('hidden');
        toggle.setAttribute('aria-expanded', 'true');
        var input = drawer.querySelector('input[type="search"]');
        if (input) input.focus();
      } else {
        drawer.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });

    onGlobal('searchKeys', document, 'keydown', function (e) {
      if (e.key === 'Escape' && !drawer.hasAttribute('hidden')) {
        drawer.setAttribute('hidden', '');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          // Return focus to the control that opened the drawer, rather than
          // dropping it on <body>.
          toggle.focus();
        }
      }
    });
  }

  /* --------------------------------------------- Transparent header ----- */
  function initStickyHeader() {
    var header = $('.header--transparent');
    if (!once(header, 'Sticky')) return;
    var update = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 40);
    };
    update();
    onGlobal('stickyScroll', window, 'scroll', update, { passive: true });
  }

  /* ---------------------------------------------------------- Marquee --- */
  function initMarquee() {
    $$('.marquee__track').forEach(function (track) {
      if (track.dataset.duplicated === 'true') return;
      track.innerHTML += track.innerHTML;
      track.dataset.duplicated = 'true';
    });
  }

  /* ----------------------------------------------------- Video embeds --- */
  function initVideos() {
    $$('.video-embed__play').forEach(function (button) {
      if (!once(button, 'Video')) return;
      on(button, 'click', function () {
        var wrapper = button.closest('.video-embed');
        var template = wrapper && wrapper.querySelector('template');
        if (!template) return;
        wrapper.innerHTML = template.innerHTML;
        var frame = wrapper.querySelector('iframe, video');
        if (frame && frame.tagName === 'VIDEO') frame.play();
      });
    });
  }

  /* ------------------------------------------------- Quantity widgets --- */
  function initQuantity(scope) {
    $$('.quantity', scope || document).forEach(function (widget) {
      if (widget.dataset.bound === 'true') return;
      widget.dataset.bound = 'true';
      var input = widget.querySelector('input');
      $$('button', widget).forEach(function (button) {
        on(button, 'click', function () {
          var step = button.dataset.action === 'increase' ? 1 : -1;
          var min = parseInt(input.min || '1', 10);
          var next = (parseInt(input.value, 10) || min) + step;
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* --------------------------------------------------- Product galley --- */
  function initProductGallery() {
    var gallery = $('[data-product-gallery]');
    if (!once(gallery, 'Gallery')) return;
    var main = $('[data-gallery-main] img', gallery);
    $$('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      on(thumb, 'click', function () {
        if (!main) return;
        // Clear the old srcset first, or the browser keeps showing the old image.
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        else main.removeAttribute('srcset');
        main.src = thumb.dataset.src;
        main.alt = thumb.dataset.alt || '';
        $$('[data-gallery-thumb]', gallery).forEach(function (t) {
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* -------------------------------------------------- Variant picking --- */
  function initVariants() {
    $$('[data-variant-picker]').forEach(function (picker) {
      if (!once(picker, 'Variants')) return;
      var scope = picker.closest('.shopify-section') || document;
      var form = picker.closest('form') || scope.querySelector('form[data-product-form]');
      var sectionId = picker.dataset.section;
      var variants;
      try {
        variants = JSON.parse($('[data-variant-json="' + sectionId + '"]').textContent);
      } catch (e) {
        return;
      }

      var idInput = form ? form.querySelector('input[name="id"]') : null;
      // The input ships disabled for the no-JS fallback; JS takes it over here.
      if (idInput) idInput.disabled = false;
      var priceEl = document.querySelector('[data-product-price="' + sectionId + '"]');
      var submit = form ? form.querySelector('[data-add-to-cart]') : null;
      var submitText = submit ? submit.querySelector('span') : null;
      var strings = picker.dataset;

      function currentOptions() {
        return $$('input:checked', picker).map(function (input) { return input.value; });
      }

      function matchVariant(options) {
        return variants.filter(function (variant) {
          return options.every(function (option, index) {
            return variant.options[index] === option;
          });
        })[0];
      }

      function update() {
        var variant = matchVariant(currentOptions());

        if (!variant) {
          if (submit) {
            submit.setAttribute('disabled', 'disabled');
            submit.dataset.unavailable = 'true';
            if (submitText) submitText.textContent = strings.unavailableText || 'Unavailable';
          }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;

        if (submit) {
          if (variant.available) {
            submit.removeAttribute('disabled');
            submit.dataset.unavailable = 'false';
            if (submitText) submitText.textContent = strings.addText || 'Add to cart';
          } else {
            submit.setAttribute('disabled', 'disabled');
            submit.dataset.unavailable = 'true';
            if (submitText) submitText.textContent = strings.soldOutText || 'Sold out';
          }
        }

        if (variant.featured_media_id) {
          var thumb = document.querySelector('[data-gallery-thumb][data-media-id="' + variant.featured_media_id + '"]');
          if (thumb) thumb.click();
        }

        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      $$('input', picker).forEach(function (input) { on(input, 'change', update); });
      update();
    });
  }

  /* --------------------------------------------------- Add to cart AJAX -- */
  function initCartForms() {
    $$('[data-product-form]').forEach(function (form) {
      if (!once(form, 'Cart')) return;
      var pending = false;

      function message(text) {
        var box = form.querySelector('[data-cart-error]');
        if (!box) {
          box = document.createElement('p');
          box.setAttribute('data-cart-error', '');
          box.setAttribute('role', 'alert');
          box.className = 'form-status form-status--error';
          form.appendChild(box);
        }
        box.textContent = text;
      }

      on(form, 'submit', function (event) {
        var button = form.querySelector('[data-add-to-cart]');
        if (!window.fetch || !button) return; // plain form post, no JS
        event.preventDefault();
        // One request at a time. A second click while the first is in flight
        // is ignored, never queued.
        if (pending) return;
        pending = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        var old = form.querySelector('[data-cart-error]');
        if (old) old.remove();

        function settle() {
          pending = false;
          // Respect the variant picker: a variant chosen mid-request may be sold out.
          button.disabled = button.dataset.unavailable === 'true';
          button.removeAttribute('aria-busy');
        }

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(serialize(form))
        })
          .then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) {
                var err = new Error(data.description || data.message || 'Unable to add this item.');
                err.rejected = true;
                throw err;
              }
              return data;
            });
          })
          .then(
            function () {
              // The add is confirmed. Nothing after this point may add again.
              settle();
              document.dispatchEvent(new CustomEvent('cart:added'));
              var link = document.querySelector('[data-cart-link]');
              if (link) {
                link.classList.add('is-pulsing');
                setTimeout(function () { link.classList.remove('is-pulsing'); }, 900);
              }
              refreshCartCount().catch(function () { /* count catches up on next page load */ });
            },
            function (err) {
              settle();
              // Never resubmit: if the response was lost the item may already be in the cart.
              message(err && err.rejected
                ? err.message
                : 'We could not confirm the cart update. Please check your cart before trying again.');
            }
          );
      });
    });
  }

  function serialize(form) {
    var data = {};
    new FormData(form).forEach(function (value, key) {
      if (key === 'quantity' || key === 'id') {
        data[key] = value;
      } else if (key.indexOf('properties[') === 0) {
        data.properties = data.properties || {};
        data.properties[key.slice(11, -1)] = value;
      }
    });
    return data;
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        });
        return cart;
      });
  }

  /* ---------------------------------------------------------- Cart page -- */
  function initCartPage() {
    var cartForm = $('[data-cart-form]');
    if (!once(cartForm, 'CartPage')) return;
    $$('[data-cart-quantity]', cartForm).forEach(function (input) {
      on(input, 'change', function () { cartForm.submit(); });
    });
  }

  /* ------------------------------------------------- Collection sorting -- */
  function initSortBy() {
    var select = $('[data-sort-by]');
    if (!once(select, 'Sort')) return;
    on(select, 'change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ------------------------------------------------- Reduced motion ------ */
  function initReducedMotion() {
    if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    $$('.artist-hero__media video, .hero__media video').forEach(function (video) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  /* ---------------------------------------------------------- Bootstrap -- */
  function init() {
    initReducedMotion();
    initMobileMenu();
    initSearch();
    initStickyHeader();
    initMarquee();
    initVideos();
    initQuantity();
    initProductGallery();
    initVariants();
    initCartForms();
    initCartPage();
    initSortBy();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Re-bind when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
